import { useState, useEffect } from "react";
import { usePaymentEcho } from "../../hooks/usePaymentEcho";
import { useAuth } from "../../global-components/src";
import { useMessage } from "../../global-components/src";
import axios from "axios";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { Clipboard, CreditCard, RefreshCw, Info, Wallet as WalletIcon } from "lucide-react";
import Screen from "../Layouts/Screen/Screen";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { formatCurrency } from "../../global-components/src/utils/numbers";
import { errorMessage } from "../../utils/TextManagerTwo";
import { useNavigate } from "react-router-dom";
import { PaymentTypes } from "../../global-components/src/types";
import "./wallet-deposit.css";
import { useTranslation } from 'react-i18next';

const QUICK_AMOUNTS = [20, 50, 100, 200];

const WalletDeposit = () => {
    const { token, user } = useAuth();
    const { setMessage } = useMessage();
    const { t } = useTranslation();
    const [amount, setAmount] = useState<string>("0");
    const [gateway, setGateway] = useState<PaymentTypes>("gerencianet_pix");
    const [metadata, setMetadata] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [loadingVerify, setLoadingVerify] = useState(false);
    const [transaction, setTransaction] = useState<any>(() => {
        const saved = localStorage.getItem("transactionWallet");
        return saved ? JSON.parse(saved) : null;
    });
    const [copiado, setCopiado] = useState(false);

    // Para QR PIX
    const [qrCode, setQrCode] = useState<string | null>(() => {
        const saved = localStorage.getItem("qrCodeWallet");
        return saved ? saved : null;
    });
    const [copyPaste, setCopyPaste] = useState<string | null>(() => {
        const saved = localStorage.getItem("copyPasteWallet");
        return saved ? saved : null;
    });
    const [errorsInput, setErrorsInput] = useState<{ amount?: string[] }>({});

    useEffect(() => {
        if (qrCode) localStorage.setItem("qrCodeWallet", qrCode);
        if (copyPaste) localStorage.setItem("copyPasteWallet", copyPaste);
        if (transaction) localStorage.setItem("transactionWallet", JSON.stringify(transaction));
    }, [qrCode, copyPaste, transaction]);

    const submitDeposit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        setErrorsInput({});
        setMessage("", "error");

        try {
            let valueFormmater = amount;
            valueFormmater = valueFormmater.replace(/R\$\s*/g, "");
            valueFormmater = valueFormmater.replace(/\s+/g, "");
            valueFormmater = valueFormmater.replace(/\./g, "");
            valueFormmater = valueFormmater.replace(/,/g, ".");
            let valueNumber = Number(valueFormmater);

            let errors: Record<string, string[]> = {};
            if (!valueNumber) {
                errors.amount = [t("errors.input.required", { field: t("generics.value") })];
            }
            if (valueNumber < 1) {
                errors.amount = [t("wallet.deposit.minValue")];
            }

            if (Object.keys(errors).length > 0) {
                throw { response: { data: { errors } } };
            }

            const response = await axios.post(
                `${PACOCA_API_URL}/wallet/deposit`,
                { amount: valueNumber, gateway, metadata },
                { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } }
            );

            setTransaction(response.data.transaction);
            if (response.data.gateway_data?.qrcode) {
                setQrCode(response.data.gateway_data.qrcode);
                setCopyPaste(response.data.gateway_data.copyPaste);
            }
        } catch (err: any) {
            const messageError = errorMessage(err);

            if (err?.response?.data.errors) {
                setErrorsInput(err.response.data.errors);
            } else {
                setMessage(messageError, "error");
            }
            console.error(messageError);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const verifyTransaction = async (manual = false) => {
        if (!transaction) return;
        if (manual) setLoadingVerify(true);

        try {
            const response = await axios.post(
                `${PACOCA_API_URL}/wallet/verify`,
                { gateway_txid: transaction.gateway_txid, gateway: transaction.gateway },
                { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } }
            );

            if (response.data.status === "completed") {
                setMessage(`Pagamento confirmado. Saldo atual: R$ ${response.data.balance}`, "success");
                cancel();
                navigate("/carteira");
                return;
            }

            if (manual) {
                setMessage(`Pagamento não confirmado, espere mais alguns minutos`, "error");
            }
        } catch (err: any) {
            if (manual) {
                const messageError = errorMessage(err);
                setMessage(messageError, "error");
                console.error(err);
            }
        } finally {
            setLoadingVerify(false);
        }
    };

    usePaymentEcho({
        userId: user?.id,
        token,
        onStatusUpdated: (payload) => {
            if (payload.status === "completed" && payload.type === "deposit" && payload.gateway_txid === transaction?.gateway_txid) {
                void verifyTransaction();
            }
        },
    });

    const copyLink = async () => {
        if (!copyPaste) return;
        try {
            await navigator.clipboard.writeText(copyPaste);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        } catch (err) {
            console.error(err);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAmount(formatCurrency(event.target.value));
    };

    const handleQuickAmount = (value: number) => {
        setAmount(value.toFixed(2).replace(".", ","));
        setErrorsInput({});
    };

    const cancel = () => {
        setErrorsInput({});
        setCopyPaste(null);
        setQrCode(null);
        setLoading(false);
        setTransaction(null);
        setLoadingVerify(false);

        localStorage.removeItem("qrCodeWallet");
        localStorage.removeItem("copyPasteWallet");
        localStorage.removeItem("transactionWallet");
    };

    return (
        <Screen title={t("wallet.deposit.title")} >
            <form className="bank-tx" onSubmit={submitDeposit}>
                {!qrCode && (
                    <>
                        <div className="bank-notice">
                            <span className="bank-notice-icon">
                                <Info size={16} />
                            </span>
                            <div>
                                <div className="bank-notice-title">Uso da carteira</div>
                                <p className="bank-notice-text">
                                    Os valores depositados são destinados exclusivamente ao uso dentro da
                                    plataforma: compra de verificado, molduras, transferência entre usuários
                                    (enviar café) e anúncios.
                                    <br />
                                    <strong>Não é possível realizar saques.</strong>
                                </p>
                            </div>
                        </div>

                        <div className="bank-amount-block">
                            <input
                                type="text"
                                value={amount}
                                onChange={handleChange}
                                placeholder="0,00"
                                className="bank-amount-input"
                                autoFocus
                            />
                        </div>
                        {errorsInput.amount && (
                            <div role="alert" className="bank-error" style={{ textAlign: "center", marginTop: -10, marginBottom: 14 }}>
                                {errorsInput.amount[0]}
                            </div>
                        )}

                        <div className="bank-quick-row">
                            {QUICK_AMOUNTS.map((value) => (
                                <button
                                    type="button"
                                    key={value}
                                    className="bank-chip"
                                    onClick={() => handleQuickAmount(value)}
                                >
                                    R$ {value}
                                </button>
                            ))}
                        </div>

                        <div className="bank-payment-row">
                            <span className="bank-payment-icon">
                                <WalletIcon size={17} />
                            </span>
                            <div>
                                <div className="bank-payment-title">Pix</div>
                                <div className="bank-payment-sub">Outras formas de pagamento em breve</div>
                            </div>
                        </div>

                        <button type="submit" className="foc-btn foc-btn-primary" disabled={loading}>
                            {loading ? <Spinner size="20px" /> : "Depositar"}
                        </button>
                    </>
                )}

                {qrCode && (
                    <>
                        <div className="bank-qr-status">
                            <span className="bank-qr-status-dot" />
                            Aguardando pagamento
                        </div>

                        <p className="bank-qr-hint">
                            Escaneie o QR Code com o app do seu banco ou copie o código Pix abaixo.
                        </p>

                        <div className="bank-qr-wrap">
                            <img src={qrCode} alt="QR Code PIX" className="bank-qr-img" />
                        </div>

                        <div className="bank-qr-actions">
                            <button type="button" className="foc-btn foc-btn-primary" onClick={copyLink}>
                                <Clipboard size={15} />
                                {copiado ? t("payment.copied") : t("payment.copyCode")}
                            </button>

                            {transaction && (
                                <button
                                    type="button"
                                    className="foc-btn foc-btn-primary"
                                    onClick={() => verifyTransaction(true)}
                                    disabled={loadingVerify}
                                >
                                    {loadingVerify ? <Spinner size="20px" /> : <><RefreshCw size={15} /> Verificar</>}
                                </button>
                            )}
                        </div>

                        <button type="button" className="bank-cancel-link" onClick={cancel}>
                            Cancelar
                        </button>
                    </>
                )}
            </form>
        </Screen>
    );
};

export default WalletDeposit;